import React, { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDown, Loader2, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../utils/axiosConfig';
import { Lectura } from './LecturasAparato';
import UsuariosAparato from './UsuariosAparato';
import SincronizacionAparato from './SincronizacionAparato';
import { esHoy, fechaCorta, hace, hora } from './formato';
import { display } from './ui';

const numero = (n) => (n == null ? '?' : Number(n).toLocaleString('es-VE'));

const TONOS = {
    rose: 'bg-rose-50 text-rose-700',
    amber: 'bg-amber-50 text-amber-700',
    cyan: 'bg-cyan-50 text-cyan-800',
    slate: 'bg-slate-100 text-slate-600',
};

const conexion = (a) => {
    if (a.conectado) {
        return `En línea desde las ${hora(a.conectado_desde)}${esHoy(a.conectado_desde) ? '' : ` del ${fechaCorta(a.conectado_desde)}`}`;
    }
    return a.ultima_actividad ? `Sin conexión, visto ${hace(a.ultima_actividad)}` : 'Nunca se ha conectado a Centro SURA';
};

// Lo que pide atención, para verlo sin abrir el aparato
const avisos = (a) => {
    const lista = [];
    const t = a.tareas || {};
    if (['usuarios', 'marcajes', 'sincronizar'].some((k) => t[k]?.estado === 'en_curso')) lista.push({ texto: 'Trabajando', tono: 'cyan', girando: true });
    const d = a.diferencias;
    if (!d) lista.push({ texto: 'Falta leer sus usuarios', tono: 'slate' });
    else if (d.faltan || d.sobran || d.completar || d.acceso) lista.push({ texto: `${d.faltan + d.sobran + d.completar + (d.acceso || 0)} cambios de personal por aplicar`, tono: 'cyan' });
    if (t.sincronizar && t.sincronizar.estado !== 'en_curso' && t.sincronizar.error) lista.push({ texto: 'El último cambio tuvo errores', tono: 'amber' });
    return lista;
};

const Bloque = ({ titulo, children }) => (
    <section className="rounded-xl bg-slate-50/80 border border-slate-100 p-4 space-y-3 min-w-0">
        <h3 className="text-sm font-semibold text-slate-900">{titulo}</h3>
        {children}
    </section>
);

const guardarAjuste = async (aparato, cambios, onCambio) => {
    try {
        await axiosInstance.patch(`/api/asistencia/aparatos/${aparato.id}`, cambios);
        onCambio();
    } catch (e) {
        toast.error(e.response?.data?.error || 'No se pudo guardar');
    }
};

// Lo que tiene guardado el aparato y las lecturas
const Memoria = ({ aparato, onCambio, onVerUsuarios }) => {
    const m = aparato.memoria || {};
    return (
        <Bloque titulo="Memoria del aparato">
            <p className="text-sm text-slate-600">
                {m.usuarios != null
                    ? <>Tiene <b className="font-semibold text-slate-900 tabular-nums">{numero(m.usuarios)}</b> usuarios ({numero(m.rostros)} con rostro{m.huellas ? `, ${numero(m.huellas)} con huella` : ''}) y <b className="font-semibold text-slate-900 tabular-nums">{numero(m.marcajes)}</b> marcajes.</>
                    : 'Lo dice cuando se conecta.'}
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
                <Lectura aparato={aparato} tipo="usuarios" onCambio={onCambio} />
                <Lectura aparato={aparato} tipo="marcajes" onCambio={onCambio} />
            </div>
            {aparato.usuarios_leidos > 0 && (
                <button type="button" onClick={onVerUsuarios}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-sura-blue hover:underline focus-visible:outline-none focus-visible:underline">
                    <Users size={14} /> Ver los {numero(aparato.usuarios_leidos)} usuarios leídos
                </button>
            )}
        </Bloque>
    );
};

// Quién falta cargar y quién sobra, según Centro SURA
const AltasBajas = ({ aparato, onCambio, onRevisar }) => {
    const d = aparato.diferencias;
    const t = aparato.tareas?.sincronizar;
    const enCurso = t?.estado === 'en_curso';
    const partes = [];
    if (d?.faltan) partes.push(`faltan ${numero(d.faltan)}${d.sin_rostro ? ` (${numero(d.sin_rostro)} sin rostro)` : ''}`);
    if (d?.completar) partes.push(`${numero(d.completar)} sin rostro en el aparato`);
    if (d?.sobran) partes.push(`sobran ${numero(d.sobran)}`);
    if (d?.acceso) partes.push(`${numero(d.acceso)} por ajustar el acceso del fin de semana`);
    return (
        <Bloque titulo="Altas y bajas">
            <p className="text-sm text-slate-600">
                {!d ? 'Primero trae los usuarios para poder comparar.'
                    : enCurso ? (
                        <span className="inline-flex items-center gap-1.5">
                            <Loader2 size={14} className="animate-spin text-cyan-500" />
                            Aplicando cambios: {numero(t.hechos)} de {numero(t.total)}
                        </span>
                    ) : partes.length ? `${partes.join(', ')}.` : 'Al día con Centro SURA.'}
            </p>
            {d && !enCurso && partes.length > 0 && (
                <button type="button" onClick={onRevisar}
                    className="h-8 px-3 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60">
                    Revisar y aplicar
                </button>
            )}
            {t && !enCurso && t.error && <p className="text-xs text-amber-700">Último cambio: {t.error}</p>}
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
                <input type="checkbox" checked={!!aparato.sincronizar_auto}
                    onChange={() => guardarAjuste(aparato, { sincronizar_auto: !aparato.sincronizar_auto }, onCambio)}
                    className="rounded border-slate-300 text-cyan-500 focus:ring-cyan-400/50" />
                Aplicarlas solas cada 12 horas
            </label>
        </Bloque>
    );
};

const Ajustes = ({ aparato, onCambio }) => (
    <Bloque titulo="Ajustes">
        <label className="flex flex-col gap-1 text-xs text-slate-500">
            Nombre
            <input
                key={aparato.nombre} defaultValue={aparato.nombre}
                onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== aparato.nombre) guardarAjuste(aparato, { nombre: v }, onCambio); }}
                onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
                className="h-9 px-3 rounded-lg border border-slate-200 bg-white text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-cyan-400/50"
            />
        </label>
        <dl className="text-xs text-slate-500 space-y-1">
            <div className="flex justify-between gap-3"><dt>Serial</dt><dd className="tabular-nums text-slate-700">{aparato.serial}</dd></div>
            {aparato.ip && <div className="flex justify-between gap-3"><dt>IP</dt><dd className="tabular-nums text-slate-700">{aparato.ip}</dd></div>}
            {aparato.firmware && <div className="flex justify-between gap-3"><dt>Firmware</dt><dd className="text-slate-700 truncate">{aparato.firmware}</dd></div>}
        </dl>
        <button type="button" onClick={() => guardarAjuste(aparato, { activo: false }, onCambio)}
            className="text-sm text-slate-400 hover:text-slate-700 focus-visible:outline-none focus-visible:underline">
            Ocultar este aparato
        </button>
    </Bloque>
);

const FilaAparato = ({ aparato: a, abierto, onAlternar, onCambio, onVerUsuarios, onRevisar }) => {
    const reducir = useReducedMotion();
    const lista = avisos(a);
    const ultimo = a.ultimo_marcaje;
    return (
        <div>
            <button
                type="button" onClick={onAlternar} aria-expanded={abierto}
                className="w-full grid grid-cols-[minmax(0,1fr)_auto] md:grid-cols-[16rem_minmax(0,1fr)_7rem_auto] items-center gap-x-5 gap-y-2 px-5 py-4 text-left hover:bg-slate-50/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cyan-400/60"
            >
                <div className="flex items-center gap-3 min-w-0">
                    <span className="relative flex w-2.5 h-2.5 shrink-0">
                        {a.conectado && <span className="absolute inset-0 rounded-full bg-cyan-400 opacity-50 animate-ping motion-reduce:animate-none" />}
                        <span className={`relative w-2.5 h-2.5 rounded-full ${a.conectado ? 'bg-cyan-400' : 'bg-slate-300'}`} />
                    </span>
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{a.nombre}</p>
                        <p className={`text-xs truncate ${a.conectado ? 'text-slate-500' : 'text-rose-600'}`}>{conexion(a)}</p>
                    </div>
                </div>
                <div className="min-w-0 hidden md:block">
                    <p className="text-sm text-slate-600 truncate">
                        {ultimo
                            ? <>Último marcaje: <span className="font-medium text-slate-900">{ultimo.persona?.nombre || `cédula ${ultimo.enroll_id}`}</span>, {hora(ultimo.fecha_hora)}{esHoy(ultimo.fecha_hora) ? '' : ` del ${fechaCorta(ultimo.fecha_hora)}`}</>
                            : 'Sin marcajes todavía'}
                    </p>
                    {lista.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {lista.map((x) => (
                                <span key={x.texto} className={`inline-flex items-center gap-1 h-5 px-2 rounded-full text-[11px] font-medium ${TONOS[x.tono]}`}>
                                    {x.girando && <Loader2 size={10} className="animate-spin" />}
                                    {x.texto}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="hidden md:block text-right">
                    <p className={`${display} text-xl font-semibold text-slate-900 tabular-nums leading-none`}>{numero(a.hoy?.recibidos || 0)}</p>
                    <p className="text-xs text-slate-500 mt-1">marcajes hoy</p>
                </div>
                <ChevronDown size={18} className={`text-slate-400 transition-transform ${abierto ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence initial={false}>
                {abierto && (
                    <motion.div
                        initial={reducir ? false : { height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                        exit={reducir ? undefined : { height: 0, opacity: 0 }} transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="px-5 pb-5 grid gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,0.8fr)]">
                            <Memoria aparato={a} onCambio={onCambio} onVerUsuarios={onVerUsuarios} />
                            <AltasBajas aparato={a} onCambio={onCambio} onRevisar={onRevisar} />
                            <Ajustes aparato={a} onCambio={onCambio} />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const AparatosPanel = ({ aparatos, onCambio }) => {
    const [abiertos, setAbiertos] = useState(new Set());
    const [verUsuarios, setVerUsuarios] = useState(null);
    const [verCambios, setVerCambios] = useState(null);

    if (aparatos.length === 0) {
        return (
            <div className="bg-white rounded-2xl border border-slate-200/80 p-12 text-center">
                <p className="text-slate-600 font-medium">No hay aparatos para mostrar</p>
                <p className="text-slate-400 text-sm mt-1">Aparecen solos la primera vez que se conectan al puerto 7790.</p>
            </div>
        );
    }

    const alternar = (id) => setAbiertos((s) => {
        const n = new Set(s);
        if (n.has(id)) n.delete(id); else n.add(id);
        return n;
    });

    return (
        <>
            <div className="bg-white rounded-2xl border border-slate-200/80 divide-y divide-slate-100 overflow-hidden">
                {aparatos.map((a) => (
                    <FilaAparato
                        key={a.id} aparato={a} abierto={abiertos.has(a.id)} onAlternar={() => alternar(a.id)} onCambio={onCambio}
                        onVerUsuarios={() => setVerUsuarios(a)} onRevisar={() => setVerCambios(a)}
                    />
                ))}
            </div>
            {verUsuarios && <UsuariosAparato aparato={verUsuarios} onCerrar={() => setVerUsuarios(null)} />}
            {verCambios && <SincronizacionAparato aparato={verCambios} onCambio={onCambio} onCerrar={() => setVerCambios(null)} />}
        </>
    );
};

export default AparatosPanel;
